import { useState, type KeyboardEvent } from 'react';
import { SendHorizonal } from 'lucide-react';
import { Button, Textarea } from '../../ui';

interface ComposerProps {
  /** True while a reply is generating; disables input + shows sending state. */
  sending: boolean;
  /** Called with the trimmed message text on submit. */
  onSend: (text: string) => void;
}

/**
 * Chat input composer. Enter sends; Shift+Enter inserts a newline. Clears on
 * successful submit. Disabled while a reply is in flight.
 */
export function Composer({ sending, onSend }: ComposerProps) {
  const [value, setValue] = useState('');

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
    setValue('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-t border-surface-border bg-surface-1/80 px-4 py-3 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <div className="flex-1">
          <Textarea
            rows={1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about a system, a flow, or a design decision…"
            className="max-h-40 min-h-[2.5rem] resize-none"
            aria-label="Message"
            disabled={sending}
          />
        </div>
        <Button
          onClick={submit}
          loading={sending}
          disabled={!value.trim()}
          leftIcon={!sending ? <SendHorizonal className="h-4 w-4" /> : undefined}
          aria-label="Send message"
        >
          Send
        </Button>
      </div>
      <p className="mx-auto mt-1.5 max-w-3xl text-[11px] text-zinc-600">
        Enter to send · Shift+Enter for a new line
      </p>
    </div>
  );
}

export default Composer;
